export function adaptClientProjection({
  clientId: _clientId,
  payload,
  propertyParts,
  topic: _topic,
}) {
  const normalizedParts = Array.isArray(propertyParts) ? propertyParts : []
  const leaf = String(normalizedParts[0] || '')

  if (isLegacyStatusMessage(leaf, payload)) {
    const state = String(payload?.state || '').toLowerCase()
    return {
      format: 'legacy',
      writes: [
        {
          payload: {
            ...payload,
            is_online: state === 'online',
          },
          propertyParts: ['status'],
        },
      ],
    }
  }

  if (isLegacyPlatformMessage(leaf, payload)) {
    return {
      format: 'legacy',
      writes: [
        {
          payload: normalizeLegacyPlatform(payload),
          propertyParts: ['device'],
        },
      ],
    }
  }

  if (isLegacyHealthMessage(leaf, payload)) {
    return {
      format: 'legacy',
      writes: [
        {
          payload: normalizeLegacyHealth(payload),
          propertyParts: ['heartbeat'],
        },
      ],
    }
  }

  if (isLegacyClientMessage(leaf, payload)) {
    return {
      format: 'legacy',
      writes: normalizeLegacyClient(payload),
    }
  }

  if (isLegacyAppMessage(leaf, payload)) {
    return {
      format: 'legacy',
      writes: normalizeLegacyApp(payload),
    }
  }

  if (isLegacyTeamviewerMessage(leaf, payload)) {
    return {
      format: 'legacy',
      writes: [
        {
          payload: normalizeLegacyTeamviewer(payload),
          propertyParts: ['teamviewer'],
        },
      ],
    }
  }

  return {
    format: 'current',
    writes: [{ payload, propertyParts: normalizedParts }],
  }
}

function isLegacyAppMessage(leaf, payload) {
  return (
    leaf === 'app' &&
    payload &&
    typeof payload === 'object' &&
    (typeof payload.exe === 'string' || typeof payload.name === 'string')
  )
}

function isLegacyClientMessage(leaf, payload) {
  return (
    leaf === 'client' &&
    payload &&
    typeof payload === 'object' &&
    (Array.isArray(payload.config_files) ||
      Array.isArray(payload.log_files) ||
      isObject(payload.python))
  )
}

function isLegacyHealthMessage(leaf, payload) {
  return (
    leaf === 'health' &&
    payload &&
    typeof payload === 'object' &&
    isObject(payload.device) &&
    isObject(payload.client)
  )
}

function isLegacyPlatformMessage(leaf, payload) {
  return (
    leaf === 'platform' &&
    payload &&
    typeof payload === 'object' &&
    isObject(payload.os) &&
    isObject(payload.platform)
  )
}

function isLegacyStatusMessage(leaf, payload) {
  return leaf === 'status' && payload && typeof payload.state === 'string'
}

function isLegacyTeamviewerMessage(leaf, payload) {
  return (
    leaf === 'teamviewer' &&
    payload &&
    typeof payload === 'object' &&
    typeof payload.id === 'string'
  )
}

function isObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeFileList(files) {
  const list = []
  const seen = new Set()
  if (!Array.isArray(files)) return list
  for (const file of files) {
    const filepath = String(file?.filepath || '').trim()
    const name = String(file?.name || filepath).trim()
    if (!filepath && !name) continue
    const identity = filepath || name
    if (seen.has(identity)) continue
    seen.add(identity)
    list.push({
      ...file,
      filepath,
      label: String(file?.label || file?.name || filepath).trim() || name,
      lang: String(file?.lang || 'text').trim() || 'text',
      modified: String(file?.modified || '--'),
      name,
    })
  }
  return list
}

function normalizeLegacyApp(payload) {
  const key = String(payload?.exe || payload?.name || 'legacy-app').trim()
  const normalizedKey = key || 'legacy-app'
  return [
    {
      payload: normalizeLegacyAppPayload(payload),
      propertyParts: ['apps', 'apps', normalizedKey],
    },
  ]
}

function normalizeLegacyAppPayload(payload) {
  return {
    ...payload,
    exe: String(payload?.exe || payload?.name || '').trim(),
    install_date: payload?.install_date_str || payload?.install_date || '--',
    is_installed: payload?.is_installed !== false,
    is_running: Boolean(payload?.is_running),
    last_seen: payload?.timestamp || null,
    pid: payload?.pid ?? null,
    uptime: payload?.uptime_str || payload?.uptime || '--',
  }
}

function normalizeLegacyClient(payload) {
  return [
    {
      payload: {
        install_date: payload?.install_date,
        install_date_str: payload?.install_date_str,
        python: isObject(payload?.python) ? payload.python : {},
        version: payload?.version,
      },
      propertyParts: ['agent'],
    },
    {
      payload: {
        files: normalizeFileList(payload?.config_files),
      },
      propertyParts: ['configs'],
    },
    {
      payload: {
        files: normalizeFileList(payload?.log_files),
      },
      propertyParts: ['logs'],
    },
  ]
}

function normalizeLegacyCpu(cpu) {
  const source = isObject(cpu) ? cpu : {}
  const percent = toNumber(source.percent, 0)
  const temperature = isObject(source.temperature) ? source.temperature : {}
  return {
    count: toNumber(source.count, 0),
    frequency: toNumber(source.frequency, 0),
    logical: toNumber(source.logical, 0),
    percent,
    percent_str: String(source.percent_str || `${percent}%`),
    processor: String(source.processor || '--'),
    temperature: {
      celsius: toNumber(temperature.celsius, 0),
      celsius_str: String(temperature.celsius_str || '--'),
      fahrenheit: toNumber(temperature.fahrenheit, 0),
      fahrenheit_str: String(temperature.fahrenheit_str || '--'),
    },
  }
}

function normalizeLegacyDisk(disk) {
  const source = isObject(disk) ? disk : {}
  const percent = toNumber(source.percent, 0)
  return {
    avail: toNumber(source.avail, 0),
    avail_str: String(source.avail_str || '--'),
    percent,
    percent_str: String(source.percent_str || `${percent}%`),
    physical: toNumber(source.physical, 0),
    physical_str: String(source.physical_str || '--'),
    used: toNumber(source.used, 0),
    used_str: String(source.used_str || '--'),
  }
}

function normalizeLegacyHealth(payload) {
  const system = isObject(payload?.device) ? payload.device : {}
  return {
    agent: isObject(payload?.client) ? payload.client : {},
    system: {
      cpu: normalizeLegacyCpu(system?.cpu),
      disk: normalizeLegacyDisk(system?.disk),
      local_datetime: normalizeLegacyLocalDateTime(system?.local_datetime),
      mem: normalizeLegacyMemory(system?.mem),
      uptime: normalizeLegacyUptime(system?.uptime),
    },
    teamviewer: isObject(payload?.teamviewer) ? payload.teamviewer : {},
  }
}

function normalizeLegacyLocalDateTime(localDateTime) {
  const source = isObject(localDateTime) ? localDateTime : {}
  return {
    ...source,
    day_short: String(source.day_short || source.day || '--'),
    is_dst: Boolean(source.is_dst),
    military_time: String(source.military_time || '--'),
    time: String(source.time || '--'),
    timezone: String(source.timezone || '--'),
  }
}

function normalizeLegacyMemory(mem) {
  const source = isObject(mem) ? mem : {}
  const percent = toNumber(source.percent, toNumber(source.usage, 0))
  return {
    avail: toNumber(source.avail, 0),
    avail_str: String(source.avail_str || '--'),
    percent,
    percent_str: String(
      source.percent_str || source.usage_str || `${percent}%`
    ),
    physical: toNumber(source.physical, 0),
    physical_str: String(source.physical_str || '--'),
    used: toNumber(source.used, 0),
    used_str: String(source.used_str || '--'),
  }
}

function normalizeLegacyPlatform(payload) {
  return {
    gpu: isObject(payload?.gpu) ? payload.gpu : {},
    network: isObject(payload?.network) ? payload.network : {},
    os: isObject(payload?.os) ? payload.os : {},
    platform: isObject(payload?.platform) ? payload.platform : {},
  }
}

function normalizeLegacyTeamviewer(payload) {
  return {
    ...payload,
    is_installed: payload?.is_installed !== false,
    is_running: Boolean(payload?.is_running),
  }
}

function normalizeLegacyUptime(uptime) {
  const source = isObject(uptime) ? uptime : {}
  return {
    ...source,
    day_short: String(source.day_short || source.day || '--'),
    time: String(source.time || '--'),
    uptime: String(source.uptime || '--'),
  }
}

function toNumber(value, fallback = 0) {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}
